import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import { signInWithGoogle } from "@/lib/oauth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, User, ArrowRight, Phone, Info, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import mascot from "@/assets/bailbuddies-icon.png";
import PhoneSignIn from "@/components/PhoneSignIn";
import { useAuthProviders } from "@/hooks/useAuthProviders";

type AuthView = "login" | "signup" | "forgot" | "phone";

// Google icon component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const isSafeInternalPath = (p: string | null): p is string =>
  !!p && /^\/(?!\/)[A-Za-z0-9_\-/?&=.%]*$/.test(p);

const Auth = () => {
  const [view, setView] = useState<AuthView>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);

  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isGoogleEnabled, isPhoneEnabled } = useAuthProviders();

  // Preserve the intended destination (e.g. OAuth consent URL) across
  // password sign-in, signup email confirm, and social OAuth round-trips.
  const nextParam = searchParams.get("next");
  const safeNext = isSafeInternalPath(nextParam) ? nextParam : null;
  const afterAuthPath = safeNext ?? "/dashboard";

  // Persist for AuthCallback (used by Google OAuth + magic-link returns).
  useEffect(() => {
    if (safeNext) {
      sessionStorage.setItem("post_auth_redirect", safeNext);
    }
  }, [safeNext]);

  // Resend cooldown ticker for the forgot-password screen
  useEffect(() => {
    if (resetCooldown <= 0) return;
    const t = setTimeout(() => setResetCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [resetCooldown]);




  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      // Direct Supabase OAuth. The helper clears any existing local session
      // and forces Google's account chooser before returning here.
      const callback = `${window.location.origin}/auth/callback${safeNext ? `?next=${encodeURIComponent(safeNext)}` : ""}`;
      const result = await signInWithGoogle(callback);


      if (result.error) throw result.error;

      if (!result.redirected) {
        // Popup flow: the session is already set, so route on ourselves.
        navigate(afterAuthPath, { replace: true });
        return;
      }

      // The browser is now navigating to Google; /auth/callback finalizes it.

    } catch (error: any) {
      let errorMessage = "Failed to sign in with Google.";
      let errorTitle = "Google Sign-In Failed";

      if (error.message?.includes("provider is not enabled") || error.error_code === "validation_failed") {
        errorTitle = "Google Sign-In Not Available";
        errorMessage = "Google sign-in hasn't been set up yet. Please use email or phone to sign in, or contact support.";
      } else if (error.message?.includes("popup_closed") || error.message?.includes("user_cancelled")) {
        errorTitle = "Sign-In Cancelled";
        errorMessage = "You closed the Google sign-in window. Try again when ready.";
      } else if (error.message?.includes("network") || error.message?.includes("fetch")) {
        errorTitle = "Connection Error";
        errorMessage = "Unable to reach Google. Please check your internet connection and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (view === "login") {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        
        // Check if email is verified
        if (data.user && !data.user.email_confirmed_at) {
          navigate("/verify-email");
        } else {
          navigate(afterAuthPath);
        }
      } else if (view === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
            },
            emailRedirectTo: `${window.location.origin}${afterAuthPath}`,
          },
        });

        if (error) throw error;
        
        toast({
          title: "Check your email! 📧",
          description: "We sent you a verification link to confirm your account.",
        });
        navigate("/verify-email");
      }
    } catch (error: any) {
      let message = error.message;
      if (error.message.includes("User already registered")) {
        message = "This email is already registered. Try signing in instead.";
      } else if (error.message.includes("Email not confirmed")) {
        navigate("/verify-email");
        return;
      }
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendResetEmail = async (isResend = false) => {
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: isResend ? "Another link sent 📬" : "Reset link sent! 📧",
        description: `Check ${email} for your reset link. Peek in spam if it's hiding.`,
      });
      setResetSent(true);
      setResetCooldown(30);
    } catch (error: any) {
      const raw = String(error?.message ?? "").toLowerCase();
      let description = "We couldn't send that email just now. Give it a moment and try again.";
      if (raw.includes("rate") || raw.includes("too many") || error?.status === 429) {
        description = "Whoa, speedy — too many requests. Wait a minute, then try again.";
      } else if (raw.includes("invalid") && raw.includes("email")) {
        description = "That email address doesn't look right. Double-check the spelling.";
      } else if (raw.includes("network") || raw.includes("fetch")) {
        description = "We couldn't reach our servers. Check your connection and try again.";
      }
      toast({ title: "Couldn't send the email", description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendResetEmail(false);
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-20 left-10 w-72 h-72 bg-purple-300/30 dark:bg-purple-500/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300/30 dark:bg-blue-500/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-300/20 dark:bg-pink-500/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Sparkle particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + (i % 3) * 30}%`,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.3,
            }}
          />
        ))}
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo & Mascot */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="relative inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <img 
              src={mascot} 
              alt="BailBuddies Ghost" 
              className="w-24 h-auto mx-auto drop-shadow-2xl"
            />
            {/* Glow effect */}
            <div className="absolute inset-0 bg-purple-500/30 blur-2xl rounded-full -z-10" />
          </motion.div>
          <h1 className="text-4xl font-display font-bold bg-primary bg-clip-text text-transparent">
            BailBuddies
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {view === "login" && "Welcome back, ghost! 👻"}
            {view === "signup" && "Join the ghost squad 👻"}
            {view === "forgot" && "Let's get you back in 🔐"}
            {view === "phone" && "Sign in with your phone 📱"}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-white/50 dark:border-gray-700/50 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-display text-2xl">
                {view === "login" && "Sign In"}
                {view === "signup" && "Create Account"}
                {view === "forgot" && "Reset Password"}
                {view === "phone" && "Phone Sign In"}
              </CardTitle>
              <CardDescription>
                {view === "login" && "Enter your credentials to continue"}
                {view === "signup" && "Start planning unforgettable moments"}
                {view === "forgot" && "Enter your email to receive a reset link"}
                {view === "phone" && "We'll send you a verification code"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {view === "phone" ? (
                <PhoneSignIn onBack={() => setView("login")} />
              ) : view === "forgot" ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-background/50 border-border/50 focus:border-purple-400 transition-colors"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-xl bg-primary hover:from-purple-700 hover:via-blue-600 hover:to-pink-600 font-display text-lg shadow-lg shadow-purple-500/25 transition-all duration-300"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending link...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        Send Reset Link
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    )}
                  </Button>

                  {resetSent && (
                    <div className="rounded-xl border-2 border-border bg-muted/40 p-3 text-center space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Didn't land in your inbox? Check spam, then send another.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={loading || resetCooldown > 0}
                        onClick={() => sendResetEmail(true)}
                        data-testid="resend-reset-email"
                        className="w-full h-11 rounded-xl"
                      >
                        {resetCooldown > 0 ? `Resend in ${resetCooldown}s` : "Resend reset email"}
                      </Button>
                    </div>
                  )}



                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={() => setView("login")}
                      className="text-sm text-muted-foreground hover:text-purple-600 transition-colors"
                    >
                      Back to <span className="font-semibold text-purple-600">Sign In</span>
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {/* Google Sign In Button - Only shown when enabled */}
                  {isGoogleEnabled ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading}
                      className="w-full h-12 rounded-xl bg-white dark:bg-gray-800 border-border/50 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-foreground transition-colors mb-4"
                    >
                      {googleLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          Connecting...
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <GoogleIcon />
                          Continue with Google
                        </div>
                      )}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 p-3 mb-4 rounded-xl bg-muted/50 border border-border/50 text-sm text-muted-foreground">
                      <Info className="w-4 h-4 shrink-0" />
                      <span>Google sign-in is not currently enabled.</span>
                    </div>
                  )}

                  {/* Phone Sign In — hidden while Twilio A2P verification is pending */}
                  {isPhoneEnabled && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setView("phone")}
                      className="w-full h-12 rounded-xl bg-white dark:bg-gray-800 border-border/50 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-foreground transition-colors mt-3"
                    >
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-purple-500" />
                        Continue with Phone
                      </div>
                    </Button>
                  )}

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border/50" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">or continue with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {view === "signup" && (
                      <motion.div
                        className="relative"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                      >
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Full Name"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="pl-10 h-12 rounded-xl bg-background/50 border-border/50 focus:border-purple-400 transition-colors"
                          required
                        />
                      </motion.div>
                    )}
                    
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 rounded-xl bg-background/50 border-border/50 focus:border-purple-400 transition-colors"
                        required
                      />
                    </div>

                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                      <PasswordInput
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl bg-background/50 border-border/50 focus:border-purple-400 transition-colors"
                        required
                      />
                    </div>

                    {view === "login" && (
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => setView("forgot")}
                          className="text-sm text-muted-foreground hover:text-purple-600 transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 rounded-xl bg-primary hover:from-purple-700 hover:via-blue-600 hover:to-pink-600 font-display text-lg shadow-lg shadow-purple-500/25 transition-all duration-300"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {view === "login" ? "Signing in..." : "Creating account..."}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {view === "login" ? "Sign In" : "Create Account"}
                          <ArrowRight className="w-5 h-5" />
                        </div>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={() => setView(view === "login" ? "signup" : "login")}
                      className="text-sm text-muted-foreground hover:text-purple-600 transition-colors"
                    >
                      {view === "login" ? (
                        <>Don't have an account? <span className="font-semibold text-purple-600">Sign up</span></>
                      ) : (
                        <>Already have an account? <span className="font-semibold text-purple-600">Sign in</span></>
                      )}
                    </button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Tagline */}
        <motion.p 
          className="text-center text-sm text-muted-foreground mt-6 flex items-center justify-center gap-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Sparkles className="w-4 h-4 text-purple-500" />
          Plan it. Lock it. Celebrate it.
        </motion.p>
      </div>
    </div>
  );
};

export default Auth;